import React, { useEffect } from 'react';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { Heading } from '@atlassian/learning-components';
import { Outlet, Navigate } from 'react-router-dom';

import { ALM_INTEGRATION_FF } from 'src/constants';
import { getMyLearningUrl } from 'src/utils/getUrl';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { useReferrer } from 'src/hooks/useReferrer';
import { sendScreenEvent } from 'src/utils/analytics';

import { MyLearningPageContentSections } from './constants';
import { MyLearningTabs } from './children/MyLearningTabs';

export const MyLearningLayoutWrapper: React.FC = () => {
  const isALMIntegrationEnabled = FeatureGates.checkGate(ALM_INTEGRATION_FF);
  const { user, isLoading: isLoadingAtlassianUser, hasError } = useAtlassianUser();
  const { referrerPath, isInitialized } = useReferrer();

  useEffect(() => {
    if (!isLoadingAtlassianUser && !hasError && isInitialized) {
      sendScreenEvent('myLearningScreen', { learningReferrerPath: referrerPath }, user?.account_id);
    }
  }, [isLoadingAtlassianUser, hasError, isInitialized, referrerPath, user?.account_id]);

  // Feature flag check - redirect to legacy route if not enabled
  if (!isALMIntegrationEnabled) {
    return <Navigate to={getMyLearningUrl(MyLearningPageContentSections.IN_PROGRESS)} replace />;
  }

  return (
    <main data-testid="my-learning-page" css={basePageStyles}>
      <section data-testid="my-learning-section" css={sharedSectionContainerStyles}>
        <Heading level={2}>My learning</Heading>
        <MyLearningTabs />
        <Outlet />
      </section>
    </main>
  );
};

const basePageStyles = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const sharedSectionContainerStyles = css({
  maxWidth: '1232px',
  width: '100%',
  padding: '176px 0 104px',
  margin: '0 auto',

  '@media (max-width: 1279px)': {
    padding: `${token('space.300')} ${token('space.200')} ${token('space.800')} ${token('space.200')}`,
  },

  [media.below.sm]: {
    padding: `${token('space.300')} ${token('space.200')} ${token('space.800')} ${token('space.200')}`,
  },
  [media.only.sm]: {
    padding: `${token('space.400')} ${token('space.300')} ${token('space.800')} ${token('space.300')}`,
  },
});
